import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';

type Params<T extends GenericFilter> = {
  filters: T[];
  filterKind: string | null;
  availableFilterValues: string[];
  setFilters: (filters: T[]) => void;
  isExclude?: boolean;
};

export function useFilterSelectAllV2<T extends GenericFilter>({
  filters,
  filterKind,
  availableFilterValues,
  setFilters,
  isExclude: isExcludeOverride,
}: Params<T>) {
  const hasSelectedAll = useMemo(() => {
    if (!filterKind) return false;
    const targetFilter = filters.find((f) => f.column === filterKind);
    if (!targetFilter) return false;
    return targetFilter.select_all ?? false;
  }, [filterKind, filters]);

  const [allValuesSnapshot, setAllValuesSnapshot] = useState<string[]>([]);

  useEffect(() => {
    setAllValuesSnapshot([]);
  }, [filterKind]);

  useEffect(() => {
    if (hasSelectedAll && availableFilterValues.length > 0) {
      setAllValuesSnapshot((prev) => (prev.length === 0 ? availableFilterValues : prev));
    }
    if (!hasSelectedAll) {
      setAllValuesSnapshot((prev) => (prev.length === 0 ? prev : []));
    }
  }, [hasSelectedAll, availableFilterValues]);

  const handleOnCheckSelectAll = useCallback(
    (checked: CheckedState) => {
      if (!filterKind) return;

      const replaceFilterPreservingOrder = (nextFilter: T | null) => {
        let replaced = false;
        const nextFilters = filters.flatMap((filter) => {
          if (filter.column !== filterKind) return [filter];
          replaced = true;
          return nextFilter ? [nextFilter] : [];
        });

        if (!replaced && nextFilter) {
          nextFilters.push(nextFilter);
        }

        return nextFilters;
      };

      if (checked) {
        const existingFilter = filters.find((f) => f.column === filterKind);
        const isExclude = isExcludeOverride ?? existingFilter?.is_exclude ?? false;

        const targetFilter = {
          column: filterKind,
          is_exclude: isExclude,
          select_all: checked !== 'indeterminate' ? checked : false,
          values: availableFilterValues?.slice(0, Math.min(availableFilterValues.length, 5)).map((av) => av) ?? [],
        } as T;
        setFilters(replaceFilterPreservingOrder(targetFilter));
      } else {
        setFilters(replaceFilterPreservingOrder(null));
      }
    },
    [availableFilterValues, filterKind, filters, isExcludeOverride, setFilters],
  );

  return { handleOnCheckSelectAll, hasSelectedAll, allValuesSnapshot };
}
